import { useState, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useOrganization } from '@clerk/clerk-react';
import { buildSupabaseAuthHeaders } from '@/lib/auth';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';

export type PortalioStatus =
  | 'connected'
  | 'expired'
  | 'disconnected'
  | 'not_configured'
  | 'loading';

export function usePortalioStatus() {
  const { organization } = useOrganization();
  const [status, setStatus] = useState<PortalioStatus>('loading');
  const [lastChecked, setLastChecked] = useState<Date | null>(null);

  useEffect(() => {
    if (!organization?.id) {
      setStatus('not_configured');
      return;
    }

    checkStatus();

    let isActive = true;
    let supabaseClient: any = null;
    let channel: any = null;

    const setupSubscription = async () => {
      try {
        supabaseClient = await getAuthenticatedSupabaseClient();
        if (!isActive) {
          return;
        }

        channel = supabaseClient
          .channel(`portal-status-${organization.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'portal_integrations',
              filter: `clerk_org_id=eq.${organization.id}`,
            },
            () => {
              checkStatus();
            },
          )
          .subscribe();
      } catch (error) {
        logger.error('Failed to set up Portal.io status subscription:', error);
      }
    };

    setupSubscription();

    return () => {
      isActive = false;
      if (channel && supabaseClient) {
        supabaseClient.removeChannel(channel);
      }
    };
  }, [organization?.id]);

  const checkStatus = async () => {
    if (!organization?.id) {
      setStatus('not_configured');
      return;
    }

    try {
      const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
      const headers = await buildSupabaseAuthHeaders({ 'Content-Type': 'application/json' });

      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/portal_integrations?clerk_org_id=eq.${organization.id}`,
        {
          headers,
        },
      );

      if (!response.ok) {
        setStatus('disconnected');
        return;
      }

      const data = await response.json();

      if (!data || data.length === 0) {
        // No integration record exists
        setStatus('not_configured');
        return;
      }

      const integration = data[0];

      // Check if basic credentials exist
      if (!integration.api_key || !integration.secret_key) {
        setStatus('not_configured');
        return;
      }

      // Check if integration is inactive
      if (!integration.is_active) {
        setStatus('disconnected');
        return;
      }

      // Check if token is expired
      if (integration.token_expiry) {
        const expiryDate = new Date(integration.token_expiry);
        const now = new Date();

        if (expiryDate <= now) {
          setStatus('expired');
          return;
        }
      }

      // Check connection_status field (set by edge function)
      // NOTE: Edge function stores tokens in user_token_encrypted, NOT user_token
      // So we check connection_status as the primary indicator
      if (integration.connection_status) {
        switch (integration.connection_status) {
          case 'active':
          case 'connected':
            setStatus('connected');
            break;
          case 'expired':
            setStatus('expired');
            break;
          case 'disconnected':
          case 'error':
            setStatus('disconnected');
            break;
          default:
            // Unknown status - treat as disconnected
            setStatus('disconnected');
        }
      } else {
        // No connection_status means never authenticated
        setStatus('disconnected');
      }

      setLastChecked(new Date());
    } catch (error) {
      logger.error('Error checking Portal.io status:', error);
      setStatus('disconnected');
    }
  };

  // Refresh status manually
  const refreshStatus = () => {
    setStatus('loading');
    checkStatus();
  };

  return {
    status,
    lastChecked,
    refreshStatus,
    isLoading: status === 'loading',
  };
}
